/**
 * Obliczenia czasu dla siatki grafiku.
 *
 * Backend zwraca znaczniki ze strefą, przeglądarka renderuje je w strefie
 * użytkownika. Szkoła działa w jednej strefie i tak z niej korzysta, więc
 * nie przeliczamy niczego ręcznie — polegamy na `Intl` i obiekcie `Date`.
 */

import type { Weekday } from '@/api/types'

/** Zakres godzin pokazywany w siatce. Szkoła pracuje popołudniami. */
export const GRID_START_HOUR = 7
export const GRID_END_HOUR = 22
export const MINUTES_IN_GRID = (GRID_END_HOUR - GRID_START_HOUR) * 60

/** Wysokość jednej godziny w pikselach — steruje gęstością siatki. */
export const HOUR_HEIGHT = 64

const timeFormatter = new Intl.DateTimeFormat('pl-PL', {
  hour: '2-digit',
  minute: '2-digit',
})

const dayLongFormatter = new Intl.DateTimeFormat('pl-PL', {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
  year: 'numeric',
})

const dayShortFormatter = new Intl.DateTimeFormat('pl-PL', {
  weekday: 'short',
  day: 'numeric',
  month: 'numeric',
})

const monthFormatter = new Intl.DateTimeFormat('pl-PL', { month: 'long', year: 'numeric' })
const monthShortFormatter = new Intl.DateTimeFormat('pl-PL', { month: 'long' })

export const formatTime = (value: string | Date): string =>
  timeFormatter.format(typeof value === 'string' ? new Date(value) : value)

export const formatDayLong = (day: Date): string => dayLongFormatter.format(day)
export const formatDayShort = (day: Date): string => dayShortFormatter.format(day)
export const formatMonth = (day: Date): string => monthFormatter.format(day)
export const formatMonthShort = (day: Date): string => monthShortFormatter.format(day)

/** „2026-08-17” — format oczekiwany przez API. */
export function toIsoDate(day: Date): string {
  const year = day.getFullYear()
  const month = String(day.getMonth() + 1).padStart(2, '0')
  const date = String(day.getDate()).padStart(2, '0')
  return `${year}-${month}-${date}`
}

export function addDays(day: Date, amount: number): Date {
  const result = new Date(day)
  result.setDate(result.getDate() + amount)
  return result
}

export function addMonths(day: Date, amount: number): Date {
  const result = new Date(day)
  // Ustawienie dnia na 1 przed przesunięciem: bez tego 31 marca minus miesiąc
  // dałoby 3 marca, bo luty nie ma 31 dni.
  result.setDate(1)
  result.setMonth(result.getMonth() + amount)
  return result
}

export function startOfDay(day: Date): Date {
  const result = new Date(day)
  result.setHours(0, 0, 0, 0)
  return result
}

/** Poniedziałek tygodnia, w którym leży podana data. */
export function startOfWeek(day: Date): Date {
  const result = startOfDay(day)
  const shift = (result.getDay() + 6) % 7 // niedziela = 0 → 6
  result.setDate(result.getDate() - shift)
  return result
}

export function endOfWeek(day: Date): Date {
  return addDays(startOfWeek(day), 6)
}

export function startOfMonth(day: Date): Date {
  const result = startOfDay(day)
  result.setDate(1)
  return result
}

export function endOfMonth(day: Date): Date {
  const result = startOfMonth(day)
  result.setMonth(result.getMonth() + 1)
  result.setDate(0)
  return result
}

export function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  )
}

export function isToday(day: Date): boolean {
  return isSameDay(day, new Date())
}

/** Siedem kolejnych dni od poniedziałku. */
export function weekDays(anchor: Date): Date[] {
  const monday = startOfWeek(anchor)
  return Array.from({ length: 7 }, (_, index) => addDays(monday, index))
}

/** Pełne tygodnie pokrywające miesiąc — siatka kalendarza zawsze ma 7 kolumn. */
export function monthGridDays(anchor: Date): Date[] {
  const first = startOfWeek(startOfMonth(anchor))
  const last = endOfWeek(endOfMonth(anchor))
  const days: Date[] = []
  for (let day = first; day <= last; day = addDays(day, 1)) {
    days.push(day)
  }
  return days
}

export function hoursInGrid(): number[] {
  return Array.from(
    { length: GRID_END_HOUR - GRID_START_HOUR + 1 },
    (_, index) => GRID_START_HOUR + index,
  )
}

/** Pozycja i wysokość bloku zajęć w siatce, w procentach jej wysokości. */
export function blockGeometry(startsAt: string, endsAt: string): { top: number; height: number } {
  const start = new Date(startsAt)
  const end = new Date(endsAt)

  // Doba różnicy między początkiem a końcem: rezerwacja sali potrafi trwać
  // cały dzień i skończyć się nazajutrz. Bez tego składnika sama godzina
  // zegarowa dałaby koniec **przed** początkiem, a całodniowy remont zwinąłby
  // się do najniższego dopuszczalnego paska. Zajęcia doby nie przekraczają,
  // więc dla nich składnik zawsze wynosi zero.
  const dob = Math.round(
    (startOfDay(end).getTime() - startOfDay(start).getTime()) / (24 * 60 * 60 * 1000),
  )

  const startMinutes = start.getHours() * 60 + start.getMinutes() - GRID_START_HOUR * 60
  const endMinutes = end.getHours() * 60 + end.getMinutes() + dob * 24 * 60 - GRID_START_HOUR * 60

  // Zajęcia poza zakresem siatki przycinamy, żeby nie wypadły poza kontener.
  const top = Math.max(0, startMinutes)
  const bottom = Math.min(MINUTES_IN_GRID, endMinutes)

  return {
    top: (top / MINUTES_IN_GRID) * 100,
    // Minimalna wysokość: blok 15-minutowy musi pomieścić godzinę i nazwisko.
    height: (Math.max(30, bottom - top) / MINUTES_IN_GRID) * 100,
  }
}

/** Pozycja bloku w poziomie, gdy w jednej kolumnie sąsiaduje ich kilka. */
export interface Lane<T> {
  item: T
  lane: number
  lanes: number
}

/**
 * Rozkłada nachodzące na siebie bloki na równoległe tory.
 *
 * W oddziale z kilkoma salami nauczyciele pracują jednocześnie, więc ich bloki
 * zachodzą na siebie w pionie i bez tego podziału jeden zasłaniałby drugi.
 * Każdy blok trafia do pierwszego toru wolnego o jego godzinie — układ czyta się
 * tak jak w arkuszu szkoły, tylko bez ręcznego rozsuwania kolumn.
 *
 * Znaczniki czasu porównujemy jako tekst: wszystkie przychodzą z API w UTC
 * z tym samym formatem, więc kolejność leksykalna jest kolejnością czasową,
 * a parsowanie dat niczego by tu nie poprawiło.
 */
export function assignLanes<T extends { starts_at: string; ends_at: string }>(
  items: T[],
): Lane<T>[] {
  const sorted = [...items].sort((a, b) => a.starts_at.localeCompare(b.starts_at))
  const laneEnds: string[] = []

  const placed = sorted.map((item) => {
    const lane = laneEnds.findIndex((end) => end <= item.starts_at)
    if (lane === -1) {
      laneEnds.push(item.ends_at)
      return { item, lane: laneEnds.length - 1 }
    }
    laneEnds[lane] = item.ends_at
    return { item, lane }
  })

  return placed.map((entry) => ({ ...entry, lanes: laneEnds.length }))
}

/**
 * Rozkłada bloki na tory osobno w każdej grupie nachodzących na siebie zajęć.
 *
 * `assignLanes` liczy tory dla całego wejścia naraz, więc jedna kolizja zwęża
 * wszystkie bloki kolumny — w dniu z siedmioma lekcjami i jedną parą w tej
 * samej godzinie reszta dnia robiłaby się bez powodu o połowę węższa. Tutaj
 * grupujemy najpierw to, co faktycznie na siebie zachodzi, i dzielimy
 * szerokość tylko w obrębie takiej grupy.
 */
export function assignOverlapLanes<T extends { starts_at: string; ends_at: string }>(
  items: T[],
): Lane<T>[] {
  // Kolejność wejścia decyduje o torach przy równych godzinach rozpoczęcia —
  // `assignLanes` sortuje stabilnie, więc wywołujący może nią sterować.
  const sorted = [...items].sort((a, b) => a.starts_at.localeCompare(b.starts_at))
  const wynik: Lane<T>[] = []
  let grupa: T[] = []
  let koniecGrupy = ''

  const domknij = () => {
    if (grupa.length > 0) wynik.push(...assignLanes(grupa))
    grupa = []
    koniecGrupy = ''
  }

  for (const item of sorted) {
    // Nowa grupa zaczyna się tam, gdzie blok nie dotyka już żadnego z poprzednich.
    if (grupa.length > 0 && item.starts_at >= koniecGrupy) domknij()
    grupa.push(item)
    if (item.ends_at > koniecGrupy) koniecGrupy = item.ends_at
  }
  domknij()

  return wynik
}

const ISO_TO_WEEKDAY: Record<number, Weekday> = {
  1: 'MONDAY',
  2: 'TUESDAY',
  3: 'WEDNESDAY',
  4: 'THURSDAY',
  5: 'FRIDAY',
  6: 'SATURDAY',
  7: 'SUNDAY',
}

export function weekdayOf(day: Date): Weekday {
  return ISO_TO_WEEKDAY[day.getDay() === 0 ? 7 : day.getDay()]
}

/** Łączy datę i godzinę „HH:MM” w znacznik ISO ze strefą przeglądarki. */
export function combineToIso(day: Date, hhmm: string): string {
  const [hours, minutes] = hhmm.split(':').map(Number)
  const result = new Date(day)
  result.setHours(hours, minutes, 0, 0)
  return result.toISOString()
}

/** „14:30” z obiektu Date — do wypełnienia pola typu `time`. */
export function toTimeInput(value: Date | string): string {
  const day = typeof value === 'string' ? new Date(value) : value
  return `${String(day.getHours()).padStart(2, '0')}:${String(day.getMinutes()).padStart(2, '0')}`
}
